import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Plus, Copy, KeyRound } from "lucide-react";

const INDUSTRIES = ["Manufacturing", "Legal", "Healthcare", "Finance", "Education", "Retail", "Technology", "Government", "General"];
const inputCls = "w-full bg-[#1B1B28] border hairline px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/60";

export default function NewCustomerButton() {
  const { user } = useAuth();
  const { addTenant, switchTenant } = useTenant();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({ name: "", industry: "General", contact_email: "", l3: false });
  const create = useMutation({
    mutationFn: () => api.post("/tenants", form).then((r) => r.data),
    onSuccess: (t) => { addTenant(t, false); setCreated(t); toast.success(`${t.name} onboarded`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create customer"),
  });
  if (user?.role !== "admin") return null;

  const reset = () => { setOpen(false); setTimeout(() => { setCreated(null); setForm({ name: "", industry: "General", contact_email: "", l3: false }); }, 200); };
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const installCmd = created ? `irm "${window.location.origin}/api/agent/install.ps1?key=${created.enrollment_key}&server=${encodeURIComponent(window.location.origin)}" | iex` : "";

  return (
    <>
      <button data-testid="new-customer-btn" onClick={() => setOpen(true)}
        className="border hairline hover:border-white/30 hover:bg-white/5 text-zinc-200 px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">
        <Plus size={13} /> New customer
      </button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : reset())}>
        <DialogContent data-testid="new-customer-dialog" className="bg-[#1B1B28] border-white/10 text-zinc-100 sm:max-w-[520px] rounded-none">
          {!created ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-extrabold tracking-tight flex items-center gap-2"><Building2 size={16} className="text-cyan-300" /> Onboard a new MSP customer</DialogTitle>
                <DialogDescription className="text-zinc-500 text-xs">Creates an isolated tenant with its own enrollment key, response policy and dashboards.</DialogDescription>
              </DialogHeader>
              <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
                <Field label="Customer name *"><input data-testid="customer-name" required minLength={2} autoFocus className={inputCls} value={form.name} onChange={set("name")} placeholder="Globex Corporation" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Industry">
                    <select data-testid="customer-industry" className={inputCls} value={form.industry} onChange={set("industry")}>
                      {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                    </select>
                  </Field>
                  <Field label="Security contact"><input data-testid="customer-email" type="email" className={inputCls} value={form.contact_email} onChange={set("contact_email")} placeholder="soc@globex.com" /></Field>
                </div>
                <label className={`flex items-start gap-3 border p-3 cursor-pointer ${form.l3 ? "border-red-500/50 bg-red-950/20" : "hairline"}`}>
                  <Switch data-testid="customer-l3" checked={form.l3} onCheckedChange={set("l3")} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">Enable Level 3 autonomous ransomware isolation</div>
                    <div className="text-[11px] text-zinc-500">Off by default — the AI will propose actions for approval instead.</div>
                  </div>
                </label>
                <button data-testid="customer-submit" type="submit" disabled={create.isPending || form.name.trim().length < 2}
                  className="w-full bg-cyan-400 text-[#15151F] hover:bg-cyan-300 px-3 py-2.5 text-[11px] font-mono uppercase tracking-widest font-bold disabled:opacity-50 transition-colors duration-150">
                  {create.isPending ? "CREATING…" : "CREATE CUSTOMER"}
                </button>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-extrabold tracking-tight flex items-center gap-2"><KeyRound size={16} className="text-emerald-400" /> {created.name} is live</DialogTitle>
                <DialogDescription className="text-zinc-500 text-xs">Tenant <span className="font-mono text-zinc-300">{created.slug}</span> · Enroll the first machine to start receiving telemetry.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <CopyBox testid="created-enrollment-key" label="Enrollment key" value={created.enrollment_key} />
                <CopyBox testid="created-install-cmd" label="Windows one-line installer (Admin PowerShell)" value={installCmd} />
                <button data-testid="customer-done" onClick={() => { switchTenant(created.id); reset(); }}
                  className="w-full border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest transition-colors duration-150">Go to dashboard</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    {children}
  </label>
);

const CopyBox = ({ label, value, testid }) => (
  <div className="border hairline p-3 bg-black/40">
    <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    <div className="flex items-start gap-2">
      <code data-testid={testid} className="font-mono text-[11px] text-cyan-300 break-all flex-1">{value}</code>
      <button onClick={() => navigator.clipboard.writeText(value).then(() => toast.success("Copied"))} className="text-zinc-400 hover:text-white shrink-0"><Copy size={13} /></button>
    </div>
  </div>
);
